import { useState } from "react";
import { api, errorText } from "../../api";
import { useToast } from "../../lib/toast";
import { useApi } from "../../lib/useApi";
import type { Category, KanzleiBeleg } from "./types";

// Sammel-Scan aufteilen (server/src/modules/belege/aufteilen.ts): Die Mitarbeiterin legt fest, auf welcher Seite ein
// neuer Beleg beginnt, in welchen Ordner er gehört und wie er heißt. Ist die KI für den Mandanten an, füllt
// „KI-Vorschlag“ die Liste vor; gespeichert wird erst mit „Aufteilen“.

interface Stand {
  seiten: number;
  grund: string | null;
  ordner: Category;
  ki: { ok: boolean; grund: string | null };
}
interface Teil {
  von: number;
  ordner: Category;
  name: string;
}

const ORDNER: [Category, string][] = [
  ["eingang", "Eingangsbelege"],
  ["ausgang", "Ausgangsbelege"],
  ["kontoauszug", "Kontoauszug"],
];

// Seiten eines Teils: bis zum Anfang des nächsten
const bisVon = (teile: Teil[], i: number, seiten: number) =>
  (teile[i + 1]?.von ?? seiten + 1) - 1;

export function Aufteilen({
  b,
  onDone,
  onCancel,
}: {
  b: KanzleiBeleg;
  onDone(): void;
  onCancel(): void;
}) {
  const toast = useToast();
  const res = useApi<Stand>(`/api/kanzlei/beleg/${b.id}/aufteilen`);
  const s = res.data;
  const [teile, setTeile] = useState<Teil[] | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [busy, setBusy] = useState<"ki" | "go" | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!s) return <p className="note auf">{res.error ?? "Lädt …"}</p>;
  if (s.grund) {
    return (
      <div className="auf">
        <p className="note">{s.grund}</p>
        <button type="button" className="btn sm" onClick={onCancel}>
          Zurück
        </button>
      </div>
    );
  }
  const liste = teile ?? [
    { von: 1, ordner: s.ordner, name: "" },
    {
      von: 2,
      ordner: s.ordner === "kontoauszug" ? "eingang" : s.ordner,
      name: "",
    },
  ];
  const setze = (i: number, p: Partial<Teil>) =>
    setTeile(liste.map((t, j) => (j === i ? { ...t, ...p } : t)));
  // neuer Beleg hinter dem letzten, der noch mehr als eine Seite hat
  const dazu = () => {
    const i = [...liste.keys()]
      .reverse()
      .find((j) => bisVon(liste, j, s.seiten) > liste[j]!.von);
    if (i === undefined) return;
    const von = liste[i]!.von + 1;
    setTeile([
      ...liste.slice(0, i + 1),
      { von, ordner: "eingang", name: "" },
      ...liste.slice(i + 1),
    ]);
  };
  // Anfang verschieben: zwischen dem Teil davor und dem danach
  const anfang = (i: number, v: number) => {
    const min = liste[i - 1]!.von + 1;
    const max = (liste[i + 1]?.von ?? s.seiten + 1) - 1;
    if (Number.isInteger(v)) setze(i, { von: Math.min(max, Math.max(min, v)) });
  };

  const vorschlag = async () => {
    setBusy("ki");
    setError(null);
    try {
      const r = await api.post<{
        teile: { von: number; ordner: Category; name: string | null }[];
        hinweis: string | null;
      }>(`/api/kanzlei/beleg/${b.id}/aufteilen/vorschlag`);
      setTeile(
        r.teile.map((t) => ({
          von: t.von,
          ordner: t.ordner,
          name: t.name ?? "",
        })),
      );
      setHinweis(r.hinweis);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };
  const los = async () => {
    setBusy("go");
    setError(null);
    try {
      const r = await api.post<{ ids: string[]; text: string }>(
        `/api/kanzlei/beleg/${b.id}/aufteilen`,
        {
          teile: liste.map((t, i) => ({
            von: t.von,
            bis: bisVon(liste, i, s.seiten),
            ordner: t.ordner,
            name: t.name.trim(),
          })),
        },
      );
      toast(`Aufgeteilt in ${r.ids.length} Belege: ${r.text}`);
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(null);
    }
  };

  return (
    <div className="auf">
      <div className="auf-h">
        <b>Seiten aufteilen</b>
        <span className="note">
          {s.seiten} Seiten · {b.contactName} sieht danach die einzelnen Belege,
          der Sammel-Scan bleibt gespeichert
        </span>
      </div>
      {s.ki.ok ? (
        <button
          type="button"
          className="btn sm"
          onClick={() => void vorschlag()}
          disabled={!!busy}
        >
          {busy === "ki" ? "KI liest …" : "KI-Vorschlag"}
        </button>
      ) : (
        s.ki.grund && (
          <p className="note">{s.ki.grund} Bitte selbst aufteilen.</p>
        )
      )}
      {hinweis && <p className="note">KI: {hinweis}</p>}
      <ol className="auf-l">
        {liste.map((t, i) => {
          const bis = bisVon(liste, i, s.seiten);
          return (
            <li key={i}>
              <label>
                ab Seite
                <input
                  type="number"
                  value={t.von}
                  min={i === 0 ? 1 : liste[i - 1]!.von + 1}
                  max={s.seiten}
                  disabled={i === 0}
                  onChange={(e) => anfang(i, Number(e.target.value))}
                  aria-label={`Beleg ${i + 1}: ab Seite`}
                />
              </label>
              <span className="note">
                {bis > t.von ? `bis ${bis}` : "eine Seite"}
              </span>
              <select
                value={t.ordner}
                onChange={(e) =>
                  setze(i, { ordner: e.target.value as Category })
                }
                aria-label={`Beleg ${i + 1}: Ordner`}
              >
                {ORDNER.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={t.name}
                maxLength={120}
                placeholder="Name, z. B. Rechnung Telekom"
                onChange={(e) => setze(i, { name: e.target.value })}
                aria-label={`Beleg ${i + 1}: Name`}
              />
              {i > 0 && (
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => setTeile(liste.filter((_, j) => j !== i))}
                  aria-label={`Beleg ${i + 1} entfernen (Seiten zum Beleg davor)`}
                >
                  ✕
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {error && (
        <p className="note lv-err" role="alert">
          {error}
        </p>
      )}
      <div className="bk-actions">
        <button
          type="button"
          className="btn sm"
          onClick={dazu}
          disabled={!!busy || liste.length >= s.seiten}
        >
          + Beleg
        </button>
        <button
          type="button"
          className="btn sm dark"
          onClick={() => void los()}
          disabled={!!busy || liste.length < 2}
        >
          {busy === "go" ? "Teilt …" : `In ${liste.length} Belege aufteilen`}
        </button>
        <button
          type="button"
          className="btn sm"
          onClick={onCancel}
          disabled={busy === "go"}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}
